import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest } from 'redux-saga/effects'
import { makeGetRequest, makePostRequest, makePutRequest, makePatchRequest } from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { toDeal, makeIdsString } from './serializers'
import { fromBoardItems } from 'utils/serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { isAdmin } from 'utils/authService'
import { DEAL_BOARD_PAGE_SIZE } from 'utils/constants'

function* fetchDealsBoardRowData(action) {
  const { filters } = action.payload
  const {
    endDate,
    startDate,
    categories,
    contractTypes,
    awarded,
    geographicalAreas,
    priorities,
    scopes,
    sizes,
    sources,
    workTypes,
    teams
  } = filters
  try {
    const rowData = yield makePostRequest('/v1/deals-board', {
      fromDate: startDate,
      toDate: endDate,
      size: DEAL_BOARD_PAGE_SIZE,
      ...(categories ? { categories: categories.map(v => v.value) } : {}),
      ...(contractTypes ? { contractTypes: contractTypes.map(v => v.value) } : {}),
      ...(awarded ? { awarded } : {}),
      ...(geographicalAreas ? { geographicalAreas: geographicalAreas.map(v => v.value) } : {}),
      ...(priorities ? { priorities: priorities.map(v => v.value) } : {}),
      ...(scopes ? { scopes: scopes.map(v => v.value) } : {}),
      ...(sizes ? { sizes: sizes.map(v => v.value) } : {}),
      ...(sources ? { sources: sources.map(v => v.value) } : {}),
      ...(workTypes ? { workTypes: workTypes.map(v => v.value) } : {}),
      ...(teams ? { teams: teams.map(v => v.value) } : {})
    })

    yield put({
      type: ACT.DEALS_BOARD_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData: fromBoardItems(rowData)
      }
    })
    yield put({
      type: ACT.DEALS_BOARD_FETCH_DEALS_HEADER,
      payload: {
        id: -1,
        startDate,
        endDate,
        categories,
        contractTypes,
        awarded,
        geographicalAreas,
        priorities,
        scopes,
        sizes,
        sources,
        workTypes,
        teams
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Opportunities',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealsBoardByUserRowData(action) {
  const { id, filters } = action.payload
  const {
    endDate,
    startDate,
    categories,
    contractTypes,
    awarded,
    geographicalAreas,
    priorities,
    scopes,
    sizes,
    sources,
    workTypes,
    teams
  } = filters
  try {
    let url = '/v1/deals-board/user'
    if (!isAdmin() || id === -1) {
      url = '/v1/deals-board'
    }

    const rowData = yield makePostRequest(url, {
      fromDate: startDate,
      toDate: endDate,
      size: DEAL_BOARD_PAGE_SIZE,
      ...(categories ? { categories: categories.map(v => v.value) } : {}),
      ...(contractTypes ? { contractTypes: contractTypes.map(v => v.value) } : {}),
      ...(awarded ? { awarded } : {}),
      ...(geographicalAreas ? { geographicalAreas: geographicalAreas.map(v => v.value) } : {}),
      ...(priorities ? { priorities: priorities.map(v => v.value) } : {}),
      ...(scopes ? { scopes: scopes.map(v => v.value) } : {}),
      ...(sizes ? { sizes: sizes.map(v => v.value) } : {}),
      ...(sources ? { sources: sources.map(v => v.value) } : {}),
      ...(workTypes ? { workTypes: workTypes.map(v => v.value) } : {}),
      ...(teams ? { teams: teams.map(v => v.value) } : {}),
      ...(!isAdmin() || id === -1 ? {} : { userId: id })
    })

    yield put({
      type: ACT.DEALS_BOARD_FOR_USER_FETCH_SUCCESS
    })

    yield put({
      type: ACT.DEALS_BOARD_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData: fromBoardItems(rowData)
      }
    })
    yield put({
      type: ACT.DEALS_BOARD_FETCH_DEALS_HEADER,
      payload: {
        id,
        startDate,
        endDate,
        categories,
        contractTypes,
        awarded,
        geographicalAreas,
        priorities,
        scopes,
        sizes,
        sources,
        workTypes,
        teams
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Opportunities',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createDeal(action) {
  const { deal, fromDashboard, cb } = action.payload
  try {
    const url = '/v1/deals'
    const response = yield makePostRequest(url, toDeal(deal))
    response.id = String(response.id)
    yield put({
      type: ACT.CREATE_DEAL_SUCCESS,
      payload: {
        deal: response,
        fromDashboard
      }
    })

    if (fromDashboard) {
      yield put({
        type: ACT.DASHBOARD_UPDATE_NEW_DEALS_COUNTER
      })
    }

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Opportunity created successfully',
        level: 'success'
      }
    })

    cb && cb(response.id)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealFollowupDate(action) {
  const { followUpDate, id, awardedCompanyId } = action.payload

  try {
    const url = `/v1/deals/${id}`
    const response = yield makePatchRequest(url, {
      followUpDate,
      ...(awardedCompanyId ? { awardedCompanyId } : {})
    })

    yield put({
      type: ACT.DEAL_FOLLOWUP_DATE_UPDATE_SUCCESS,
      payload: {
        deal: response
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Opportunity',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateDealsFollowupDate(action) {
  const { followUpDate, ids, status, startDate, endDate } = action.payload

  try {
    const url = `/v1/deals-board/followUpDateForMultipleBoardDeals/${startDate}/${endDate}?size=${DEAL_BOARD_PAGE_SIZE}`
    const response = yield makePutRequest(url, {
      followUpDate,
      dealWithCompanies: ids,
      dealStatusCode: status
    })

    yield put({
      type: ACT.DEALS_FOLLOWUP_DATE_UPDATE_SUCCESS,
      payload: {
        deals: response.content,
        status
      }
    })
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Deals updated successfully!',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Deals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchNextDealColumnPage(action) {
  const { filters, userId, laneId, page } = action.payload
  const {
    endDate,
    startDate,
    categories,
    contractTypes,
    awarded,
    geographicalAreas,
    priorities,
    scopes,
    sizes,
    sources,
    workTypes,
    teams
  } = filters
  try {
    const results = yield makePostRequest('/v1/deals-board/user/status', {
      fromDate: startDate,
      toDate: endDate,
      ...(categories ? { categories: categories.map(v => v.value) } : {}),
      ...(contractTypes ? { contractTypes: contractTypes.map(v => v.value) } : {}),
      ...(awarded ? { awarded } : {}),
      ...(geographicalAreas ? { geographicalAreas: geographicalAreas.map(v => v.value) } : {}),
      ...(priorities ? { priorities: priorities.map(v => v.value) } : {}),
      ...(scopes ? { scopes: scopes.map(v => v.value) } : {}),
      ...(sizes ? { sizes: sizes.map(v => v.value) } : {}),
      ...(sources ? { sources: sources.map(v => v.value) } : {}),
      ...(workTypes ? { workTypes: workTypes.map(v => v.value) } : {}),
      ...(teams ? { teams: teams.map(v => v.value) } : {}),
      size: DEAL_BOARD_PAGE_SIZE,
      status: laneId,
      page,
      userId
    })

    yield put({
      type: ACT.DEALS_BOARD_ADD_DEALS_TO_COLUMN,
      payload: {
        deals: makeIdsString(results.content),
        last: results.last,
        laneId,
        page
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Deals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealsHeader(action) {
  const {
    endDate,
    startDate,
    id,
    categories,
    contractTypes,
    awarded,
    geographicalAreas,
    priorities,
    scopes,
    sizes,
    sources,
    workTypes,
    teams
  } = action.payload
  try {
    const dealsHeader = yield makePostRequest('/v1/deals-board/countstatus/user', {
      userId: id,
      fromDate: startDate,
      toDate: endDate,
      ...(categories ? { categories: categories.map(v => v.value) } : {}),
      ...(contractTypes ? { contractTypes: contractTypes.map(v => v.value) } : {}),
      ...(awarded ? { awarded } : {}),
      ...(geographicalAreas ? { geographicalAreas: geographicalAreas.map(v => v.value) } : {}),
      ...(priorities ? { priorities: priorities.map(v => v.value) } : {}),
      ...(scopes ? { scopes: scopes.map(v => v.value) } : {}),
      ...(sizes ? { sizes: sizes.map(v => v.value) } : {}),
      ...(sources ? { sources: sources.map(v => v.value) } : {}),
      ...(workTypes ? { workTypes: workTypes.map(v => v.value) } : {}),
      ...(teams ? { teams: teams.map(v => v.value) } : {})
    })

    yield put({
      type: ACT.DEALS_BOARD_ADD_DEALS_HEADER_TO_COLUMNS,
      payload: {
        dealsHeader
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Deals Headers',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealsStatuses() {
  try {
    const statuses = yield makeGetRequest('/v1/dealstatus/user')

    yield put({
      type: ACT.DEAL_STATUSES_FETCH_SUCCESS,
      payload: {
        statuses
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Deal Statuses',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchOrganizationStatuses() {
  try {
    const statuses = yield makeGetRequest('/v1/dealstatus')

    yield put({
      type: ACT.ORGANIZATION_STATUSES_FETCH_SUCCESS,
      payload: {
        statuses
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Organization Statuses',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchDealTasks(action) {
  const { cardId, laneId } = action.payload

  try {
    const tasks = yield makeGetRequest(`/v1/deals-board/task-info/${cardId}`)

    yield put({
      type: ACT.DEALS_BOARD_FETCH_DEAL_TASKS_SUCCESS,
      payload: {
        tasks,
        cardId,
        laneId
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Deal Tasks',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const dealsSagas = [
  takeLatest(ACT.DEALS_BOARD_ROW_DATA_FETCH_REQUESTED, fetchDealsBoardRowData),
  takeLatest(ACT.CREATE_DEAL_REQUESTED, createDeal),
  takeLatest(ACT.DEALS_BOARD_FOR_USER_FETCH_REQUESTED, fetchDealsBoardByUserRowData),
  takeLatest(ACT.DEAL_FOLLOWUP_DATE_UPDATE_REQUESTED, updateDealFollowupDate),
  takeLatest(ACT.DEALS_FOLLOWUP_DATE_UPDATE_REQUESTED, updateDealsFollowupDate),
  takeLatest(ACT.DEAL_BOARD_FETCH_NEXT_PAGE, fetchNextDealColumnPage),
  takeLatest(ACT.DEALS_BOARD_FETCH_DEALS_HEADER, fetchDealsHeader),
  takeLatest(ACT.DEAL_STATUSES_FETCH_REQUESTED, fetchDealsStatuses),
  takeLatest(ACT.ORGANIZATION_STATUSES_FETCH_REQUESTED, fetchOrganizationStatuses),
  takeLatest(ACT.DEALS_BOARD_FETCH_DEAL_TASKS_REQUESTED, fetchDealTasks)
]

export default dealsSagas
